import { MapPin, Star, BookmarkPlus, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { authFetch } from '../utils/authFetch';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Parse cultural_metadata (JSONB string or object) and pick the best display tags
const getCulturalDisplayTags = (metadata) => {
  if (!metadata) return [];
  let m = metadata;
  if (typeof m === 'string') {
    try { m = JSON.parse(m); } catch { return []; }
  }
  // Priority order: designer_archetypes first, then subculture_vibe
  const designer = (m.designer_archetypes || []).slice(0, 1);
  const vibe = (m.subculture_vibe || []).slice(0, 1);
  const combined = [...designer, ...vibe].filter(Boolean);
  return combined.length ? combined.slice(0, 2) : (m.historian_tags || []).slice(0, 2);
};

// Format a tag for display: "Ford-Gucci-1996" → "Ford · Gucci" (trim year)
const formatTag = (tag) => {
  if (!tag) return '';
  const parts = tag.split('-').filter(p => !/^\d{4}$/.test(p)); // remove years
  return parts.slice(0, 2).join(' · ');
};

export const CandidateCard = ({
  candidate,
  isNew = false,
  compareMode = false,
  isInCompare = false,
  onToggleCompare,
  bulkMode = false,
  isSelected = false,
  onToggleBulk,
  onViewed,
  onDragStart,
  onDragEnd,
  preferredRoles = [],
}) => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPortfolio, setShowPortfolio] = useState(false);

  const name = candidate.full_name || candidate.name;
  const role = candidate.core_role || candidate.role;
  const city = candidate.location_city;
  const country = candidate.location_country;
  const location = city && country ? `${city}, ${country}` : (candidate.location || '');
  const photo = candidate.photo_url || candidate.photo;
  const ranking = candidate.overall_ranking || candidate.rating;
  const seniority = candidate.seniority;
  const portfolio = candidate.portfolio_preview || [];
  const culturalTags = getCulturalDisplayTags(candidate.cultural_metadata);

  // Contextual role-fit (present when the gallery is ranked by a brief/role)
  const roleFit = candidate.role_fit;
  const fitTier = candidate.role_fit_tier;
  const hasFit = roleFit !== undefined && roleFit !== null;
  const FIT_STYLE = {
    native: 'bg-[hsl(var(--primary))] text-white',
    strong: 'bg-[hsl(var(--primary))] text-white',
    adjacent: 'bg-foreground text-background',
    related: 'bg-white text-foreground border border-border',
    distant: 'bg-white text-muted-foreground border border-border',
  };

  // Relevance signal: does this candidate match the user's hiring focus?
  const isRelevant = preferredRoles.length > 0 && preferredRoles.some(prefRole => {
    const words = prefRole.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    return words.some(w => (role || '').toLowerCase().includes(w));
  });

  const handleClick = () => {
    if (compareMode) { onToggleCompare?.(candidate); return; }
    if (bulkMode) { onToggleBulk?.(candidate); return; }
    onViewed?.(candidate);
    navigate(`/candidate/${candidate.id}`);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (saved || saving) return;
    setSaving(true);
    try {
      const res = await authFetch(`${BACKEND}/api/dashboard/pipeline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: candidate.id, stage: 'saved' }),
      });
      if (res.status === 401) { navigate('/login'); return; }
      setSaved(true);
    } catch {}
    finally { setSaving(false); }
  };

  const handleDragStart = (e) => {
    e.dataTransfer.setData('candidateId', candidate.id);
    e.dataTransfer.setData('candidateName', name || '');
    e.dataTransfer.effectAllowed = 'copy';
    onDragStart?.(candidate);
  };

  const isDraggable = !compareMode && !bulkMode && !!onDragStart;
  const isActive = compareMode ? isInCompare : bulkMode ? isSelected : false;

  return (
    <div
      onClick={handleClick}
      draggable={isDraggable}
      onDragStart={isDraggable ? handleDragStart : undefined}
      onDragEnd={isDraggable ? () => onDragEnd?.() : undefined}
      className={`group cursor-pointer relative ${isActive ? 'ring-2 ring-foreground' : ''} ${isDraggable ? 'drag-card' : ''}`}
      onMouseEnter={() => portfolio.length > 0 && setShowPortfolio(true)}
      onMouseLeave={() => setShowPortfolio(false)}
    >
      {/* Photo - 3:4 aspect ratio */}
      <div className="relative aspect-[3/4] overflow-hidden bg-secondary mb-4">
        {photo ? (
          <img src={photo} alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={e => { if (e.currentTarget) { e.currentTarget.style.display = 'none'; if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = 'flex'; } }}
          />
        ) : null}
        {/* Fallback initials avatar — shown when photo missing or fails to load */}
        <div
          className="absolute inset-0 flex items-center justify-center bg-secondary"
          style={{ display: photo ? 'none' : 'flex' }}
        >
          <span className="text-3xl font-semibold text-muted-foreground/40" style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}>
            {name ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
          </span>
        </div>

        {/* NEW badge */}
        {isNew && (
          <div className="absolute top-3 left-3 bg-green-500 text-white text-xs px-2 py-0.5 font-medium">
            NEW
          </div>
        )}

        {/* Contextual FIT badge (when ranked by a brief/role) — takes priority */}
        {hasFit && (
          <div className={`absolute top-3 right-3 px-2 py-1 flex items-center gap-1.5 ${FIT_STYLE[fitTier] || FIT_STYLE.related}`}
            data-testid="card-fit-badge" title={`Role fit: ${roleFit}/10 (${fitTier})`}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '8px', letterSpacing: '0.12em' }} className="uppercase opacity-70">Fit</span>
            <span className="text-xs font-bold">{Number(roleFit).toFixed(1)}</span>
          </div>
        )}

        {/* Ranking badge (hidden when a fit badge is shown) */}
        {!hasFit && ranking && (
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-2 py-1 flex items-center gap-1">
            <Star className="w-3 h-3 fill-foreground text-foreground" />
            <span className="text-xs font-semibold text-foreground">{ranking}</span>
          </div>
        )}

        {/* Save / Compare / Select button */}
        {!compareMode && !bulkMode && (
          <button onClick={handleSave}
            className={`absolute bottom-3 right-3 p-1.5 backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 ${
              saved ? 'bg-foreground text-background' : 'bg-white/90 text-foreground hover:bg-foreground hover:text-background'
            }`}
            title={saved ? 'Saved' : 'Save to pipeline'}>
            {saved ? <Check className="w-3.5 h-3.5" /> : <BookmarkPlus className="w-3.5 h-3.5" />}
          </button>
        )}

        {compareMode && (
          <div className={`absolute inset-0 flex items-center justify-center transition-all ${isInCompare ? 'bg-foreground/20' : 'bg-transparent hover:bg-foreground/10'}`}>
            {isInCompare && <Check className="w-8 h-8 text-white bg-foreground rounded-full p-1.5" />}
          </div>
        )}

        {bulkMode && (
          <div className={`absolute inset-0 flex items-center justify-center transition-all ${isSelected ? 'bg-foreground/20' : 'hover:bg-foreground/10'}`}>
            {isSelected && <Check className="w-8 h-8 text-white bg-foreground rounded-full p-1.5" />}
          </div>
        )}

        {/* Portfolio hover preview */}
        {showPortfolio && portfolio.length > 0 && !compareMode && !bulkMode && (
          <div className="absolute inset-0 bg-black/80 flex flex-col justify-end p-3 pointer-events-none">
            <div className="flex items-center justify-between mb-2">
              <p className="text-white text-xs font-medium">Portfolio</p>
              {portfolio.length > 3 && (
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.1em' }}
                  className="text-white/50">
                  +{portfolio.length - 3} more
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-1">
              {portfolio.slice(0, 3).map((item, i) => {
                const isVideo = item.cover_image && /\.(mov|mp4|webm)(\?|$)/i.test(item.cover_image);
                return (
                  <div key={i} className="aspect-square overflow-hidden bg-white/10 relative flex items-center justify-center">
                    {isVideo ? (
                      <>
                        <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="white" opacity="0.7">
                            <polygon points="5,3 19,12 5,21"/>
                          </svg>
                        </div>
                      </>
                    ) : (
                      <img src={item.cover_image} alt={item.title}
                        className="w-full h-full object-cover"
                        onError={e => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    )}
                    {!isVideo && (
                      <div className="absolute inset-0 bg-zinc-800 items-center justify-center" style={{ display: 'none' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" opacity="0.4">
                          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/>
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-foreground group-hover:opacity-70 transition-opacity">{name}</h3>
        <p className="text-sm text-muted-foreground">{seniority ? `${seniority} ${role}` : role}</p>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="w-3.5 h-3.5" />
          <span>{location}</span>
        </div>

        {/* Cultural Fit Score — historian DNA chips */}
        {culturalTags.length > 0 && (
          <div className="flex items-center gap-1.5 pt-0.5 flex-wrap" data-testid="cultural-tags">
            {culturalTags.map((tag, i) => (
              <span
                key={i}
                className="text-xs text-muted-foreground/70 border border-border px-1.5 py-0.5 leading-tight"
                style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.05em' }}
                title={tag}
              >
                {formatTag(tag)}
              </span>
            ))}
          </div>
        )}

        {/* Hiring focus relevance signal */}
        {isRelevant && (
          <div className="flex items-center gap-1 pt-1" data-testid="relevance-indicator">
            <div className="w-1 h-1 bg-foreground rounded-full flex-shrink-0" />
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '8px', letterSpacing: '0.1em' }}
              className="text-muted-foreground/50 uppercase">Matches your focus</span>
          </div>
        )}
      </div>
    </div>
  );
};
